import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Listing, ListingData } from '../types';
import { useTenantPath } from '../../../hooks/useTenantPath';
import { useMetadata } from '../../../contexts/MetadataContext';
import { Lookup } from '../../../system/lookup';
import {
  ImageNotSupported,
  PlaceOutlined,
  HomeRounded,
  ApartmentRounded,
  AgricultureOutlined,
} from '@mui/icons-material';
import { SkeletonCard } from './SkeletonCard';

// Property type mapping with icons and display names
const PROPERTY_TYPE_MAP: Record<
  string,
  { icon: React.ReactElement; label: string }
> = {
  'Commercial Lease': { icon: <ApartmentRounded />, label: 'Commercial Lease' },
  'Commercial Sale': { icon: <ApartmentRounded />, label: 'Commercial Sale' },
  'Business Opportunity': {
    icon: <ApartmentRounded />,
    label: 'Business Opportunity',
  },
  'Land and Lots': { icon: <PlaceOutlined />, label: 'Land and Lots' },
  Land: { icon: <PlaceOutlined />, label: 'Land' },
  Farm: { icon: <AgricultureOutlined />, label: 'Farm' },
  Residential: { icon: <HomeRounded />, label: 'Residential' },
  'Residential Income': { icon: <HomeRounded />, label: 'Residential Income' },
  'Residential Lease': { icon: <HomeRounded />, label: 'Residential Lease' },
};

interface PropertyCardProps {
  listing: Listing | ListingData;
  isLoading?: boolean;
  isBrightTenant?: boolean;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({
  listing,
  isLoading,
  isBrightTenant = true,
}) => {
  const navigate = useNavigate();
  const { getPath } = useTenantPath();
  const { lookup } = useMetadata();

  // Check if this is a skeleton (summary only) or loading state
  const isSkeleton =
    isLoading || ('IsSummary' in listing && listing.IsSummary === true);

  // const isSkeleton = true;

  // Type guard to determine if listing is ListingData or old Listing type
  const isListingData = (
    listing: Listing | ListingData,
  ): listing is ListingData => {
    return 'Property' in listing;
  };

  // Get property type display info
  const getPropertyTypeInfo = (typeCode?: string) => {
    if (!typeCode) return null;

    // If isBrightTenant is true, use the hardcoded map
    if (isBrightTenant) {
      return PROPERTY_TYPE_MAP[typeCode] || null;
    }

    // If isBrightTenant is false, use metadata lookup
    const propertyTypeLookups = lookup(Lookup.PROPERTY_TYPE);
    if (!propertyTypeLookups) return null;

    // Find the matching lookup entry by LookupValue
    const matchingLookup = propertyTypeLookups.find(
      (l) => l.LookupValue === typeCode,
    );
    if (!matchingLookup) return null;

    // Return with the display name from lookup and default icon
    return {
      icon: PROPERTY_TYPE_MAP[matchingLookup?.DisplayValue || '']?.icon, // Default icon for non-bright tenants
      label: matchingLookup.DisplayValue,
    };
  };

  // Get status badge info - returns null if no status available
  const getStatusBadge = (): {
    label: string;
    color: string;
  } | null => {
    if (isListingData(listing)) {
      const status = listing.Property.MLS_STATUS;

      // Don't show badge if no status is populated
      if (!status) return null;

      const statusColor = getStatusColor(status);

      return {
        label: status,
        color: statusColor,
      };
    }

    return null;
  };

  // If skeleton, render loading state
  if (isSkeleton && isListingData(listing)) {
    return <SkeletonCard listing={listing} />;
  }

  // Format address
  const getAddress = () => {
    if (isListingData(listing)) {
      return listing.Property.UNPARSED_ADDRESS || 'Address not available';
    }

    // Handle old Listing type
    if (listing.UnparsedAddress) {
      return listing.UnparsedAddress;
    }

    const parts = [
      listing.StreetNumber,
      listing.StreetDirPrefix?.value,
      listing.StreetName,
      listing.StreetSuffix?.value,
    ].filter(Boolean);

    const street = parts.join(' ');
    const cityState = [
      listing.City,
      listing.StateOrProvince?.value,
      listing.PostalCode,
    ]
      .filter(Boolean)
      .join(', ');

    return [street, cityState].filter(Boolean).join(', ');
  };

  // Format price
  const formatPrice = (price?: number) => {
    if (!price) return 'No List Price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Get status color
  const getStatusColor = (status?: string) => {
    const normalizedStatus = status?.toLowerCase();

    switch (normalizedStatus) {
      case 'active':
        return 'success.main';
      case 'pending':
        return 'warning';
      case 'closed':
        return 'default.main';
      case 'expired':
      case 'withdrawn':
        return 'error';
      default:
        return 'info';
    }
  };

  // Get listing key
  const getListingKey = () => {
    if (isListingData(listing)) {
      return listing.Property.LISTING_KEY;
    }
    return listing.ListingKey?.toString() || listing.ListingId;
  };

  // Get listing identifier for navigation (prioritize ID over Key)
  const getListingIdentifier = () => {
    if (isListingData(listing)) {
      // Prioritize LISTING_KEY over LISTING_ID
      return listing.Property.LISTING_KEY || listing.Property.LISTING_ID;
    }
    return null;
  };

  // Handle navigation
  const handleViewDetails = () => {
    const identifier = getListingIdentifier();
    const path = getPath(`/edit/${identifier}`);
    navigate(path);
  };

  // Get image URL or placeholder - find media with lowest MEDIA_DISPLAY_ORDER
  const imageUrl = isListingData(listing)
    ? (() => {
        if (!listing.Media || listing.Media.length === 0) return undefined;

        // Find the media item with the lowest MEDIA_DISPLAY_ORDER
        const sortedMedia = [...listing.Media].sort(
          (a, b) => (a.MEDIA_DISPLAY_ORDER || 0) - (b.MEDIA_DISPLAY_ORDER || 0),
        );

        return sortedMedia[0]?.MEDIA_URL;
      })()
    : undefined;

  return (
    <Card
      sx={{
        height: '100%',
        width: '280px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.secondary',
        border: 1,
        borderRadius: '12px',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      {/* Image container with status badge overlay */}
      <Box sx={{ position: 'relative', mt: 3 }}>
        {imageUrl ? (
          <CardMedia
            component="img"
            height="200"
            image={imageUrl}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              borderTop: 1,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'grey.100',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <ImageNotSupported />
          </Box>
        )}

        {/* Status Badge Overlay - Only show if status exists */}
        {getStatusBadge() && (
          <Chip
            label={getStatusBadge()!.label}
            size="small"
            sx={{
              '&.MuiChip-filled': {
                backgroundColor: getStatusBadge()!.color + '.light',
              },
              color: getStatusBadge()!.color + '.dark',
              position: 'absolute',
              top: 12,
              left: 12,
              fontWeight: 600,
              textTransform: 'capitalize',
              boxShadow: 1,
              borderRadius: '4px',
            }}
          />
        )}
      </Box>
      <CardContent
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {isListingData(listing) && (
            <>
              {listing.Property.PROPERTY_TYPE && (
                <>
                  {(() => {
                    const propertyTypeInfo = getPropertyTypeInfo(
                      listing.Property.PROPERTY_TYPE,
                    );
                    return propertyTypeInfo ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'text.primary',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ display: 'flex', fontSize: 18 }}
                        >
                          {propertyTypeInfo.icon}
                        </Box>
                        <Typography sx={{ fontSize: '14px' }}>
                          {propertyTypeInfo.label}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        color="text.primary"
                        sx={{ fontSize: '14px' }}
                      >
                        {listing.Property.PROPERTY_TYPE}
                      </Typography>
                    );
                  })()}
                </>
              )}
            </>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="primary.main" gutterBottom>
            {getAddress()}
          </Typography>
          <Typography
            variant="h6"
            color="primary.main"
            component="div"
            gutterBottom
          >
            {formatPrice(
              isListingData(listing)
                ? listing.Property.LIST_PRICE
                : listing.ListPrice,
            )}
          </Typography>
          <Typography
            color="text.primary"
            display="block"
            sx={{ fontSize: '14px' }}
          >
            MLS#: {getListingKey()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
            fullWidth
            onClick={handleViewDetails}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
